import React from 'react';
import {
  View, Text, Switch, StyleSheet, ScrollView, TouchableOpacity,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import type { ProfileStackParamList } from '../../navigation/AppNavigator';
import { useSettingsStore } from '../../store';
import type { AnnouncementSetting } from '../../types';

type Props = NativeStackScreenProps<ProfileStackParamList, 'Settings'>;

const ANNOUNCEMENT_OPTIONS: Array<{ key: AnnouncementSetting; label: string; description: string }> = [
  { key: 'first_landmark', label: 'First Landmark Stats', description: 'Announce total distance, time, and average pace when you reach the first landmark' },
  { key: 'pace', label: 'Pace', description: 'Announce pace in minutes per mile' },
  { key: 'time', label: 'Time', description: 'Announce time for this segment' },
  { key: 'distance', label: 'Distance', description: 'Announce distance for this segment' },
  { key: 'vs_personal_last', label: 'vs Last Time', description: 'Compare this segment to your previous run on the same segment' },
  { key: 'vs_personal_best', label: 'vs Personal Best', description: 'Compare to your fastest time on this segment' },
  { key: 'vs_personal_avg', label: 'vs Personal Average', description: 'Compare to your average and standard deviations' },
  { key: 'vs_group_age', label: 'vs Age Group', description: 'Compare to runners in your age group (requires age in profile)' },
  { key: 'vs_group_gender', label: 'vs Gender Group', description: 'Compare to runners of same gender (requires gender in profile)' },
  { key: 'vs_group_named', label: 'vs Named Group', description: 'Compare to your primary group (e.g. running club)' },
];

export default function SettingsScreen(_props: Props) {
  const { settings, setAnnouncement, setProximityRadius, setAutopause } = useSettingsStore();

  return (
    <ScrollView style={styles.container}>
      <Text style={styles.sectionTitle}>Announcements</Text>
      <Text style={styles.sectionSub}>Choose what Landmark Run announces after each landmark segment.</Text>

      <View style={styles.card}>
        {ANNOUNCEMENT_OPTIONS.map((opt, idx) => (
          <View key={opt.key} style={[styles.row, idx < ANNOUNCEMENT_OPTIONS.length - 1 && styles.rowBorder]}>
            <View style={styles.rowText}>
              <Text style={styles.optLabel}>{opt.label}</Text>
              <Text style={styles.optDesc}>{opt.description}</Text>
            </View>
            <Switch
              value={settings.announcements[opt.key]}
              onValueChange={(v) => setAnnouncement(opt.key, v)}
              trackColor={{ true: '#1a73e8' }}
            />
          </View>
        ))}
      </View>

      <Text style={styles.sectionTitle}>Autopause</Text>
      <Text style={styles.sectionSub}>Automatically pause when you stop moving (speed below 0.5 m/s for 3 seconds).</Text>

      <View style={styles.card}>
        <View style={styles.row}>
          <View style={styles.rowText}>
            <Text style={styles.optLabel}>Autopause</Text>
            <Text style={styles.optDesc}>Pauses timer and GPS distance tracking when you slow to a stop</Text>
          </View>
          <Switch
            value={settings.autopause}
            onValueChange={(v) => setAutopause(v)}
            trackColor={{ true: '#1a73e8' }}
          />
        </View>
      </View>

      <Text style={styles.sectionTitle}>Proximity Radius</Text>
      <Text style={styles.sectionSub}>How close (meters) you must be to a landmark to trigger it.</Text>

      <View style={styles.card}>
        {[10, 20, 30, 50].map((r) => (
          <TouchableOpacity
            key={r}
            style={[styles.row, styles.rowBorder, settings.proximityRadiusMeters === r && styles.rowSelected]}
            onPress={() => setProximityRadius(r)}
          >
            <Text style={[styles.optLabel, settings.proximityRadiusMeters === r && styles.selectedText]}>
              {r} meters {r === 20 ? '(recommended)' : ''}
            </Text>
            {settings.proximityRadiusMeters === r && <Text style={styles.check}>✓</Text>}
          </TouchableOpacity>
        ))}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8f9fa' },
  sectionTitle: { fontWeight: '700', fontSize: 16, paddingHorizontal: 16, paddingTop: 24, paddingBottom: 4 },
  sectionSub: { color: '#888', fontSize: 13, paddingHorizontal: 16, marginBottom: 12 },
  card: { backgroundColor: '#fff', marginHorizontal: 16, borderRadius: 12, overflow: 'hidden' },
  row: { flexDirection: 'row', alignItems: 'center', padding: 16 },
  rowBorder: { borderBottomWidth: 1, borderColor: '#f0f0f0' },
  rowSelected: { backgroundColor: '#e8f0fe' },
  rowText: { flex: 1, paddingRight: 12 },
  optLabel: { fontSize: 15, fontWeight: '500' },
  optDesc: { fontSize: 12, color: '#888', marginTop: 2 },
  selectedText: { color: '#1a73e8', fontWeight: '700' },
  check: { color: '#1a73e8', fontWeight: '700', fontSize: 18 },
});
